/**
 * Per-tenant translation overrides editor view (O2D-PRD-092).
 *
 * Renders the `/settings/translations/:segment?` page: segment tabs
 * (`<o2d-tab-list>`), a per-segment table (`<o2d-table>`) of
 * `[key, original, override]`, one global filter with per-tab match-count
 * badges, an orphan ("veraltet") section, and a full-set save that dispatches
 * `save.translation-override` and live-refreshes via `use('de.'+tenant)`.
 *
 * Draft state is a key-addressed Map held in the VIEW (not row-indexed), so
 * edits survive tab switches and filtering. Segment switching is view-internal
 * via `_activeSegment` + `history.replaceState` — the route emits the SAME tag
 * for every `:segment`, so the single instance is reconciled, not remounted.
 *
 * Extends `AbstractO2dElement` (Light DOM, ADR-007).
 */

import { AbstractO2dElement } from '@o2d/shared';
// Side-effect import: evaluating the barrel runs the element module's
// `@customElement('o2d-tab-list')` decorator, registering the tag (the tag
// string is NOT exported — importing is what makes `<o2d-tab-list>` usable).
import '@o2d/tab-list';
// The element's own CSS (tab strip layout) used to be a separate package
// export imported here as a side effect — Stefan, 2026-08-30: ALL CSS now
// lives exclusively in horstplan's own stylesheets (no external stylesheet
// imports, no inline CSS). The equivalent rules moved to ralphy.css and, since
// 2026-10-17, sit at the end of backend/public/assets/css/translation-editor.css.
import type { O2dTab, TabSelectedDetail } from '@o2d/tab-list';
import { O2D_TAB_LIST_SELECTED } from '@o2d/tab-list';
// Side-effect import: evaluating the barrel runs the element module's
// `@customElement('o2d-table')` decorator, registering the tag (the tag string is
// NOT exported — importing is what makes `<o2d-table>` usable).
import '@o2d/table';
// The element's own CSS (grid column layout + the row-height rules), incl.
// `o2d-table[row-sizing="content"] .o2d-table__row` (this view's
// `row-sizing="content"` opts into it, turning the fixed row height into a
// `min-height` floor so a multi-line override cell can grow), used to be a
// separate package export imported here as a side effect — Stefan, 2026-08-30:
// moved to ralphy.css, now o2d-table.css, see the note above.
import type { O2dTableColumn, O2dTableSort } from '@o2d/table';
// Side-effect import: evaluating the barrel runs the element module's
// `@customElement('o2d-textarea')` decorator, registering the tag (the tag
// string is NOT exported — importing is what makes `<o2d-textarea>` usable).
import '@o2d/textarea';
// The element's own CSS (`field-sizing: content` auto-grow + the base
// `min-height`) used to be a separate package export imported here as a side
// effect — Stefan, 2026-08-30: moved to ralphy.css, now textarea.css
// (section 2), see the note above.
// Side-effect import: evaluating the component module runs its
// `@customElement('ha-input-text')` decorator, registering the tag so the
// filter field's `<ha-input-text>` is defined even if this view is loaded in
// isolation (mirrors the explicit `@o2d/*` registrations above). The component
// is also globally registered via the `components` barrel (main.ts), but the
// explicit import keeps this view self-contained.
import '../components/_inputs/ha-input-text/ha-input-text.component';
import { get, use } from 'lit-translate';
import { html } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { container } from 'tsyringe';
import { hasPrivilege } from '../helpers/privilege.helper';
import { CommandName } from '../commands/command-name.enum';
import { sanitizeTenant } from '../components/ha-app/tenant-context.helper';
import { TranslationFlattenHelper } from '../helpers/translation-flatten.helper';
import { TranslationEditorHelper } from '../helpers/translation-editor.helper';
import { CommandBus } from '../services/command-bus.service';
import { GlobalNotificationService } from '../services/global-notification.service';
import { SaveTranslationOverrideCommand } from '../commands/save-translation-override.command';
// The direct toast path components use for their OWN action outcomes — the
// closest precedent is the quicklink copy in
// ha-dialog-issue-edit-orchestrator.component.ts:618-624. Deliberately NOT a new
// `GlobalNotificationService` method: that service's emitters are message-broker
// handlers for entity lifecycle events, and a translation save is not one.
import { UiHelper } from '../helpers/ui.helper';
import { NOTIFICATION_DURATION } from '../constants';

/**
 * Keyboard-idle window (ms) for coalescing segment-URL writes. `<o2d-tab-list>`
 * fires `selected` on every arrow keystroke (selection-follows-focus), so the
 * `history.replaceState` write is debounced by this interval to avoid URL thrash
 * — N keystrokes settle into ONE history write. This is the reusable
 * keyboard-idle pattern the filter input (T5.1) will share.
 */
const SEGMENT_URL_COALESCE_MS = 250;

/**
 * Keyboard-idle window (ms) for the global filter input. Typing pushes raw
 * keystrokes into `_filterSubject`, which is debounced by this interval before
 * `_filter` (the value the table filters on and the badges recompute against) is
 * updated — so N keystrokes settle into ONE re-render/badge recomputation. Reuses
 * the same RxJS keyboard-idle idiom as the segment-URL coalescing (T3.3).
 */
const FILTER_DEBOUNCE_MS = 250;

/**
 * Bounded window (ms) for detecting a save success. The command bus resolves the
 * dispatched promise only on success (`command-bus.service.ts:142`) and neither
 * throws nor settles it on failure (`:143-145`), so a bare await would HANG on a
 * failed save. The save handler races the dispatch against this timeout: a success
 * signal within the window = saved; the timeout firing = treated as a failure.
 */
const SAVE_TIMEOUT_MS = 10_000;

/**
 * A single `<o2d-table>` row for the active segment: the base key, its original
 * `de.json` text, and the current draft override (empty string = no override).
 * The table's `rows` are `unknown[]` (no `TRow` generic — the grid-layout
 * contravariance lesson), so every column accessor casts `row as Row`.
 */
type Row = {
    key: string;
    original: string;
    override: string;
};

/** Default sort: by key, ascending. The `key` column is the single source of order. */
const KEY_SORT_ASC: O2dTableSort = { columnId: 'key', direction: 'asc' };

@customElement('ha-translation-editor-view')
export class HaTranslationEditorView extends AbstractO2dElement {

    /**
     * Segment from the `/settings/translations/:segment?` route param. A one-way
     * `.segment=` binding reconciles the single instance; it seeds
     * `_activeSegment` on FIRST arrival only (see connectedCallback), so a later
     * change must NOT re-seed and fight the controlled `_activeSegment`.
     */
    @property({ type: String })
    public segment?: string;

    /** The currently displayed segment tab (view-internal, controlled). */
    @state()
    protected _activeSegment: string | null = null;

    /** Global filter string applied across the active table. */
    @state()
    protected _filter = '';

    /** Base translations (flattened `de.json`), key-addressed. */
    @state()
    protected _base: Map<string, string> = new Map();

    /** Draft overrides (init = flattened `de.<tenant>.json`), key-addressed. */
    @state()
    protected _draft: Map<string, string> = new Map();

    /** Derived segment tabs (distinct first key-segment of the base keys). */
    @state()
    protected _segments: string[] = [];

    /** True while a save is in flight — disables the save button (no double-submit). */
    @state()
    protected _saving = false;

    /**
     * The last-saved (or initially-loaded) override snapshot the dirty guard compares
     * `_draft` against. Re-baselined to the just-saved draft on save-success so a
     * saved-then-clean view stops warning and the save button returns to non-dirty.
     */
    private _savedSnapshot: Map<string, string> = new Map();

    /**
     * FIRST-arrival guard for the async load: `de.json` + `de.<tenant>.json` are
     * fetched exactly once so a later reconnect does not re-fetch and clobber the
     * in-view draft (mirror of the `_segmentSeeded` idempotency).
     */
    private _loaded = false;

    /**
     * FIRST-arrival guard: `_activeSegment` is seeded from the route `segment`
     * exactly once (see connectedCallback). A later `.segment=` change must NOT
     * re-seed and fight the controlled `_activeSegment` (mirror of the deep-link
     * seeding in knowledge-overview-view.component.ts:90-98).
     */
    private _segmentSeeded = false;

    /**
     * Emits the active segment id on every tab selection; the subscription (see
     * connectedCallback) debounces it by `SEGMENT_URL_COALESCE_MS` before the
     * `history.replaceState` write, coalescing keystroke-driven selections.
     */
    private readonly _segmentUrlSubject = new Subject<string>();

    /**
     * Emits raw filter keystrokes; the subscription (see connectedCallback)
     * debounces them by `FILTER_DEBOUNCE_MS` before committing to `_filter`, so a
     * burst of typing triggers a single re-render/badge recomputation.
     */
    private readonly _filterSubject = new Subject<string>();

    /** Completes the coalescing subscription on disconnect (RxJS teardown). */
    private readonly _disconnected$ = new Subject<void>();

    /**
     * Warns before leaving with unsaved edits. Stable arrow field so the exact same
     * reference is removed in disconnectedCallback. Setting `returnValue` triggers the
     * browser's native confirm dialog; a clean draft leaves navigation untouched.
     */
    private readonly _onBeforeUnload = (event: BeforeUnloadEvent): void => {
        if (this._isDirty()) {
            event.preventDefault();
            event.returnValue = '';
        }
    };

    /**
     * Seeds the active-segment tab from the deep-link `segment` route param on
     * FIRST arrival only. Done here (not in willUpdate/updated) so a subsequent
     * `.segment=` reconcile does not clobber a view-internal tab switch — the
     * `_segmentSeeded` guard makes the seed idempotent across reconnects.
     */
    public connectedCallback(): void {
        super.connectedCallback();

        // Listen for view-internal tab switches. The event bubbles (composed) from
        // the child `<o2d-tab-list>` up to this host; using the exported constant
        // avoids a magic-string drift. `_onTabSelected` is a stable arrow-field so
        // the same reference is removed in disconnectedCallback.
        this.addEventListener(O2D_TAB_LIST_SELECTED, this._onTabSelected);

        // Warn on browser navigation/reload while the draft has unsaved edits.
        window.addEventListener('beforeunload', this._onBeforeUnload);

        // Coalesce the segment-URL writes: only the latest segment after a
        // keyboard-idle window reaches history.replaceState, so arrowing through
        // tabs does not push one URL write per keystroke.
        this._segmentUrlSubject
            .pipe(
                debounceTime(SEGMENT_URL_COALESCE_MS),
                distinctUntilChanged(),
                takeUntil(this._disconnected$),
            )
            .subscribe(segment => this._reflectSegmentInUrl(segment));

        // Debounce the global filter: only the latest value after a keyboard-idle
        // window reaches `_filter`, so a burst of typing settles into one re-render
        // and one badge recomputation instead of one per keystroke.
        this._filterSubject
            .pipe(
                debounceTime(FILTER_DEBOUNCE_MS),
                distinctUntilChanged(),
                takeUntil(this._disconnected$),
            )
            .subscribe(value => this._filter = value);

        if (!this._segmentSeeded && this.segment) {
            this._activeSegment = this.segment;
            this._segmentSeeded = true;
        }

        if (!this._loaded) {
            this._loaded = true;
            void this._loadTranslations();
        }
    }

    public disconnectedCallback(): void {
        this.removeEventListener(O2D_TAB_LIST_SELECTED, this._onTabSelected);
        window.removeEventListener('beforeunload', this._onBeforeUnload);
        this._disconnected$.next();
        this._disconnected$.complete();
        super.disconnectedCallback();
    }

    /**
     * Handles a view-internal tab switch from `<o2d-tab-list>`: updates the
     * controlled `_activeSegment` IMMEDIATELY (drives the table + the controlled
     * `activeId`) and routes the URL write through `_segmentUrlSubject` so the
     * `history.replaceState` is coalesced (NOT pushState/goto — a per-tab push
     * would pollute history; the deep-link entry is the sole real navigation).
     * Coalescing matters because `<o2d-tab-list>` fires `selected` on every
     * arrow keystroke (selection-follows-focus).
     */
    private readonly _onTabSelected = (event: Event): void => {
        const { id } = (event as CustomEvent<TabSelectedDetail>).detail;
        this._activeSegment = id;
        this._segmentUrlSubject.next(id);
    };

    /** Reflects the active segment into the URL without a history push. */
    private _reflectSegmentInUrl(segment: string): void {
        history.replaceState({}, '', `/settings/translations/${segment}`);
    }

    /**
     * Pushes a filter value into `_filterSubject`. The value comes from the
     * `<ha-input-text>` `value-changed` CustomEvent (`detail.value`), left
     * uncontrolled (no `.value=` binding) so the caret is never disturbed by a
     * re-render; the debounced subscription commits the settled value into
     * `_filter`, which the table filters on and the badges recompute against.
     */
    private readonly _onFilterInput = (value: string): void => {
        this._filterSubject.next(value);
    };

    /**
     * Whether the draft has unsaved edits relative to the last-saved snapshot (drives
     * the save-button disabled state + the `beforeunload` warning). Order-independent
     * and empty-override-aware — see `TranslationEditorHelper.isDirty`.
     */
    private _isDirty(): boolean {
        return TranslationEditorHelper.isDirty(this._draft, this._savedSnapshot);
    }

    /**
     * Full-set save: builds the payload from ALL non-empty draft entries across every
     * segment (incl. orphans) and dispatches `SaveTranslationOverrideCommand`. Success
     * is detected by racing the dispatch against a bounded timeout — the command bus
     * neither throws nor settles the promise on failure, so a bare await would hang.
     * On success the i18n layers are re-fetched live (`use('de.'+tenant)`, no reload)
     * and the dirty guard is re-baselined; on timeout/failure a notification surfaces
     * and the draft is kept intact. Arrow field so `@click` keeps the view as `this`.
     */
    private readonly _onSave = async (): Promise<void> => {
        if (this._saving) {
            return;
        }
        this._saving = true;
        try {
            const tenant = sanitizeTenant(window.__APP_CONTEXT__);
            const payload = TranslationEditorHelper.buildPayload(this._draft);
            const command = new SaveTranslationOverrideCommand(tenant ?? '', payload);

            const success = await this._dispatchWithTimeout(command);
            if (!success) {
                container.resolve(GlobalNotificationService).error(
                    get('translationEditor.save.errorTitle'),
                    get('translationEditor.save.errorMessage'),
                );
                return;
            }

            // Live refresh (no reload): re-fetch both i18n layers so every binding updates.
            await use(tenant ? `de.${tenant}` : `de`);
            // Re-baseline the dirty guard to the just-saved draft (saved-then-clean).
            this._savedSnapshot = new Map(this._draft);

            // Re-render so this view shows its own new texts too — the tab captions
            // in particular, which read their names through `get()` at render time.
            // `use()` above clears lit-translate's translation cache before it
            // announces the change (`lit-translate/util.js:62-67`), so a plain
            // re-render returns the freshly saved strings. That is why a full page
            // reload is not needed here: it would cost the filter, the active tab
            // and the scroll position to achieve the same thing.
            // Components bound through the `translate()` directive update on their
            // own; only `get()` call sites need this nudge.
            this.requestUpdate();

            // Only NOW is the save observably done — the dispatch returning is not
            // enough, the re-fetch above is what makes the new texts visible. A toast
            // fired earlier would claim success for work still in flight.
            //
            // `level: 'info'` and NOT `'success'`: `notification.css` styles exactly
            // two levels (`notification-type__error` :27, `notification-type__info` :31).
            // A `'success'` toast gets an icon from the map in ui.helper.ts:79-83 and no
            // styling at all.
            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('translationEditor.save.successTitle'),
                content: html`<p>${get('translationEditor.save.successMessage')}</p>`,
                duration: NOTIFICATION_DURATION,
            });
        } finally {
            this._saving = false;
        }
    };

    /**
     * Races the command dispatch against `SAVE_TIMEOUT_MS`. The dispatch resolves
     * `true` on a successful `COMMAND_SEND` and never settles on failure (see
     * `SAVE_TIMEOUT_MS`), so the timeout resolving `false` is what turns a hung/failed
     * save into a detectable non-success without ever blocking the handler.
     */
    private _dispatchWithTimeout(command: SaveTranslationOverrideCommand): Promise<boolean> {
        const dispatched = container.resolve(CommandBus).dispatchCommand(command);
        const timedOut = new Promise<boolean>(resolve => {
            window.setTimeout(() => resolve(false), SAVE_TIMEOUT_MS);
        });
        return Promise.race([dispatched, timedOut]);
    }

    /**
     * Loads the base (`de.json`) and per-tenant override (`de.<tenant>.json`) i18n
     * layers in parallel, flattens both into key-addressed Maps, and derives the
     * segment tabs. Each fetch has its OWN `.catch(() => ({}))` (mirror
     * bootstrap.ts:16-19) so a missing tenant file (404) yields an empty override
     * set — NOT a blank view. The tenant is `sanitizeTenant(window.__APP_CONTEXT__)`;
     * `null` loads base `de` only (no tenant layer).
     */
    private async _loadTranslations(): Promise<void> {
        const tenant = sanitizeTenant(window.__APP_CONTEXT__);

        const [base, overrides] = await Promise.all([
            this._fetchTranslationLayer('de'),
            tenant ? this._fetchTranslationLayer(`de.${tenant}`) : Promise.resolve<Record<string, unknown>>({}),
        ]);

        this._base = TranslationFlattenHelper.flatten(base);
        this._draft = TranslationFlattenHelper.flatten(overrides);
        this._segments = TranslationEditorHelper.deriveSegments(this._base);

        // Baseline the dirty guard to the loaded overrides (the view starts clean).
        this._savedSnapshot = new Map(this._draft);

        // Fall back to the first segment when there was no deep-link seed.
        if (this._activeSegment === null) {
            this._activeSegment = this._segments[0] ?? null;
        }
    }

    /** Fetch one i18n JSON layer; a missing/invalid file yields an empty object. */
    private async _fetchTranslationLayer(language: string): Promise<Record<string, unknown>> {
        return fetch(`/assets/i18n/${language}.json`)
            .then(res => res.json())
            .catch(() => ({}));
    }

    protected render(): TemplateResult {
        // View-level privilege gate (defense-in-depth on top of the route
        // enter-guard, e.g. if the tag is instantiated directly). Uses the typed
        // hasPrivilege() enum check, NOT a raw privileges-array includes.
        // Soft today while authorization is disabled (empty privileges = admin).
        if (!hasPrivilege(CommandName.SAVE_TRANSLATION_OVERRIDE)) {
            return html``;
        }

        return html`
            <div class="view__body translation-editor">
                <div class="translation-editor__toolbar">
                    <ha-input-text
                        class="translation-editor__filter-input"
                        with-clear-button
                        placeholder=${get('translationEditor.filter.placeholder')}
                        @value-changed=${(event: CustomEvent<{ value: string }>) =>
                            this._onFilterInput(event.detail.value)}
                    ></ha-input-text>
                    <button
                        class="translation-editor__save"
                        type="button"
                        ?disabled=${this._saving || !this._isDirty()}
                        @click=${this._onSave}
                    >
                        ${get('translationEditor.save.button')}
                    </button>
                </div>
                <o2d-tab-list
                    .tabs=${this._buildTabs()}
                    .activeId=${this._activeSegment ?? undefined}
                ></o2d-tab-list>
                <o2d-table
                    row-sizing="content"
                    .columns=${this._buildColumns()}
                    .rows=${this._buildRows()}
                    .rowKey=${(row: unknown) => (row as Row).key}
                    .sort=${KEY_SORT_ASC}
                    .filter=${this._filter}
                ></o2d-table>
                ${this._renderOrphanSection()}
            </div>
        `;
    }

    /**
     * Renders the "veraltet" (orphan) section: draft override keys no longer backed
     * by a base (`de.json`) key. They are shown WITH a delete button rather than
     * silently dropped — a normal save keeps them (the full-set payload iterates the
     * whole `_draft`), so the tenant stays in control of stale overrides (G4).
     * Nothing is rendered when there are no orphans.
     */
    private _renderOrphanSection(): TemplateResult {
        const orphanKeys = TranslationEditorHelper.findOrphanKeys(this._draft, this._base);
        if (orphanKeys.length === 0) {
            return html``;
        }

        return html`
            <section class="translation-editor__orphans">
                <h2 class="translation-editor__orphans-heading">
                    ${get('translationEditor.orphan.heading')}
                </h2>
                <p class="translation-editor__orphans-hint">${get('translationEditor.orphan.hint')}</p>
                <ul class="translation-editor__orphans-list">
                    ${orphanKeys.map(key => this._renderOrphanRow(key))}
                </ul>
            </section>
        `;
    }

    /**
     * Renders a single orphan row: the stale key, its current override value, and a
     * delete button that drops the key from `_draft` (so it leaves the next save).
     */
    private _renderOrphanRow(key: string): TemplateResult {
        return html`
            <li class="translation-editor__orphan" data-key=${key}>
                <span class="translation-editor__orphan-key">${key}</span>
                <span class="translation-editor__orphan-value">${this._draft.get(key) ?? ''}</span>
                <button
                    class="translation-editor__orphan-delete"
                    type="button"
                    @click=${() => this._onOrphanDelete(key)}
                >
                    ${get('translationEditor.orphan.delete')}
                </button>
            </li>
        `;
    }

    /**
     * Deletes an orphan override: removes the key from `_draft` (fresh Map so the
     * `@state` change is observed and the section + save payload recompute). Only an
     * explicit delete removes an orphan — a normal save never silently drops it.
     */
    private _onOrphanDelete(key: string): void {
        const draft = new Map(this._draft);
        draft.delete(key);
        this._draft = draft;
    }

    /**
     * The three table columns: key (sortable), original (`de.json` text), and the
     * override. Every accessor casts `row as Row` (the table's `rows` are `unknown[]`).
     * The override column renders an editable `<o2d-textarea>` (see
     * `_renderOverrideCell`) that writes straight into the key-addressed `_draft`;
     * the table itself stays editing-agnostic. `filterValue` keeps the global
     * filter matching on the draft text even though the cell content is an input.
     */
    private _buildColumns(): O2dTableColumn[] {
        return [
            {
                id: 'key',
                header: get('translationEditor.column.key'),
                sortable: true,
                filterValue: row => (row as Row).key,
            },
            {
                id: 'original',
                header: get('translationEditor.column.original'),
                filterValue: row => (row as Row).original,
            },
            {
                id: 'override',
                header: get('translationEditor.column.override'),
                filterValue: row => (row as Row).override,
                cellRenderer: row => this._renderOverrideCell(row as Row),
            },
        ];
    }

    /**
     * Renders the editable override cell: `<o2d-textarea>`, controlled via
     * `.value` from the key-addressed `_draft` (the single source of truth). The
     * element emits a `value-changed` CustomEvent with `detail.value` (NOT a
     * native `@input`), which writes the value back into `_draft` — the same
     * contract the previous `<ha-input-text>` used, so the binding and the
     * handler are unchanged by the swap. No `label` is passed — the column header
     * labels the field, and a label would add a second line to every row.
     *
     * A textarea, not a text input: a translation may legitimately contain line
     * breaks, and `<input type="text">` cannot hold one at all (it is the element
     * that blocks them, not any trimming in the handler). The element's own
     * stylesheet auto-grows it (`field-sizing: content`), which only takes effect
     * because the table opts into content-sized rows (`row-sizing="content"`).
     *
     * Caret: the element binds its inner `<textarea>.value` from its own `value`
     * property and sets that property synchronously in its `@input` handler, so a
     * parent re-render finds the property unchanged and does not reset the caret
     * (same controlled `.value` + `@value-changed` pattern as
     * ha-form-knowledge-filters.component.ts:60-65). The keyed `repeat` in
     * `<o2d-table>` reuses the element node across re-renders, so focus survives.
     */
    private _renderOverrideCell(row: Row): TemplateResult {
        return html`
            <o2d-textarea
                class="translation-editor__override-input"
                .value=${this._draft.get(row.key) ?? ''}
                @value-changed=${(event: CustomEvent<{ value: string }>) =>
                    this._onOverrideInput(row.key, event.detail.value)}
            ></o2d-textarea>
        `;
    }

    /**
     * Persists an override edit into `_draft` (key-addressed, so it survives tab and
     * filter changes). Reassigns a fresh Map so the `@state` change is observed and
     * downstream derivations (match-count badges, dirty guard, save payload) recompute.
     */
    private _onOverrideInput(key: string, value: string): void {
        this._draft = new Map(this._draft).set(key, value);
    }

    /**
     * Builds the rows for the active segment: every base key whose first segment
     * matches `_activeSegment`, paired with its original text and the current draft
     * override (key-addressed, so it survives tab/filter changes).
     */
    private _buildRows(): Row[] {
        const rows: Row[] = [];
        for (const [key, original] of this._base) {
            if (TranslationEditorHelper.segmentOf(key) === this._activeSegment) {
                rows.push({ key, original, override: this._draft.get(key) ?? '' });
            }
        }
        return rows;
    }

    /**
     * Maps the derived segments into `<o2d-tab-list>` tab configs. `O2dTab` has no
     * badge field, so while a filter is active the per-tab match count is folded
     * into `label` (a `TemplateResult`) — a hit that lives only in another segment
     * stays discoverable via that segment's badge. With no filter the label is the
     * bare segment id (no badge noise). Counts are computed across ALL segments
     * against `_base`/`_draft`/`_filter` (same OR-match the `<o2d-table>` uses).
     */
    /**
     * The tab caption for a segment: its translated name, falling back to the raw
     * key.
     *
     * The fallback is not decoration. `lit-translate` renders an unknown key as
     * `[the.key]` (its default `empty` handler, `lit-translate/util.js:9`), so a
     * namespace added to `de.json` without a caption here would show
     * `[translationEditor.segment.foo]` — worse than the bare name it replaced.
     */
    private _segmentLabel(segment: string): string {
        const key = `translationEditor.segment.${segment}`;
        const label = get(key);
        return label === `[${key}]` ? segment : label;
    }

    private _buildTabs(): O2dTab[] {
        if (this._filter === '') {
            return this._segments.map(segment => ({ id: segment, label: this._segmentLabel(segment) }));
        }

        const counts = TranslationEditorHelper.countMatchesPerSegment(this._base, this._draft, this._filter);
        return this._segments.map(segment => ({
            id: segment,
            label: this._renderTabLabel(segment, counts.get(segment) ?? 0),
        }));
    }

    /** Renders a segment tab label with its filter match-count badge. */
    private _renderTabLabel(segment: string, count: number): TemplateResult {
        return html`${this._segmentLabel(segment)} <span class="translation-editor__tab-badge">${count}</span>`;
    }
}
